import { useCallback, useEffect, useEffectEvent, useRef, useState } from 'react'
import { useGetWorkFlowsByStatusFn, useLocation, useMonitoringServiceProviderLocation } from '../../../../api/apiQueries'
import { WorkflowStatus } from '../../../../api/models'
import { useAtomValue, useSetAtom } from 'jotai'
import { provisioningWorkflowStartedByLocationAtom } from '../../../../atoms'
import { hasDiscrepancies } from '../../../../utils/valueComparisonUtils'
import { LoggerName, namedLogger } from '../../../../utils/logger'

const logger = namedLogger(LoggerName.usePendingWorkflowStatus)

const POLLING_INTERVAL_MS = 5000
const MAX_POLLING_DURATION_MS = 60000

/**
 * This hook determines whether there is a pending provisioning workflow for a given location.
 *
 * Here's the logic:
 *
 * - "location" refers to the data from `useLocation()`.
 * - "mspLocation" refers to the data from `useMonitoringServiceProviderLocation()`.
 *
 * 1. Start polling when either of the following are true (but the initial polling starts after a 5 second delay):
 * - There are address discrepancies between the location and mspLocation.
 * - There are contact discrepancies between the location and mspLocation.
 * - the value of the `provisioningWorkflowStartedByLocationAtom` for this location is `true`
 * - there is a provisioning workflow in progress when the component mounts
 *
 * 2. Poll every 5 seconds for:
 * - Active provisioning workflows.
 * - Fresh location data
 * - Fresh mspLocation data
 *
 * 3. Stop polling when either of these is true:
 * - There are no address/contact discrepancies AND there are no active provisioning workflows.
 * - More than 1 minute has elapsed AND there is still no active provisioning workflow.
 *
 * 4. After polling stops, reset `provisioningWorkflowStartedByLocationAtom` for that location to `false`.
 *
 * 5. return true if either of the following is true:
 * - `provisioningWorkflowStartedByLocationAtom` is `true`.
 * - There is an active provisioning workflow.
 *
 * If the polling times out after 1 minute, we stop polling as described in step 3 and set the atom to false as described in step 4.
 * Polling can be restarted again if the provisioningWorkflowStartedByLocationAtom is flipped back to true again.
 *
 */
export function usePendingWorkflowStatus(locationId?: string): boolean {
  const getWorkFlowsByStatus = useGetWorkFlowsByStatusFn()
  const locationQuery = useLocation(locationId)
  const mspLocationQuery = useMonitoringServiceProviderLocation(locationId ?? '')

  const startedByLocation = useAtomValue(provisioningWorkflowStartedByLocationAtom)
  const setStartedByLocation = useSetAtom(provisioningWorkflowStartedByLocationAtom)

  const pollingStartedAtRef = useRef<number | null>(null)
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const pollingTimedOutRef = useRef(false)
  const isPollingInFlightRef = useRef(false)

  const [activeWorkflowLocationId, setActiveWorkflowLocationId] = useState<string | null>(null)

  const workflowIsActive = activeWorkflowLocationId === locationId

  const poll = useEffectEvent(async () => {
    if (!locationId || isPollingInFlightRef.current) return

    isPollingInFlightRef.current = true
    const elapsedPollingTime = pollingStartedAtRef.current ? Date.now() - pollingStartedAtRef.current : 0

    try {
      /*
       * 2. Poll every 5 seconds for:
       * - Active provisioning workflows.
       * - Fresh location data
       * - Fresh mspLocation data
       */
      const [workflows, latestLocationResult, latestMspLocationResult] = await Promise.all([
        getWorkFlowsByStatus(locationId, WorkflowStatus.Running),
        locationQuery.refetch(),
        mspLocationQuery.refetch()
      ])

      /*
       * 3. Stop polling when either of these is true:
       * - There are no address/contact discrepancies and there are no active provisioning workflows.
       * - More than 1 minute has elapsed and there is still no active provisioning workflow.
       */

      const activeWorkflowExists = workflows.length > 0
      setActiveWorkflowLocationId(activeWorkflowExists ? locationId : null)

      const discrepancyExists = hasDiscrepancies(
        latestLocationResult.data ?? locationQuery.data,
        latestMspLocationResult.data ?? mspLocationQuery.data,
        true
      )

      // There are no address/contact discrepancies AND there are no active provisioning workflows.
      if (!activeWorkflowExists && !discrepancyExists) {
        stopPolling()
        return
      }

      // More than 1 minute has elapsed AND there is still no active provisioning workflow.
      if (elapsedPollingTime >= MAX_POLLING_DURATION_MS && !activeWorkflowExists) {
        pollingTimedOutRef.current = true
        setActiveWorkflowLocationId(null)
        stopPolling()
      }
    } catch (error) {
      logger.error(`Error during polling for locationId ${locationId}`, error as Error)
      // if we continue to get errors, at least we'll stop polling after 1 minute.
      // This will allow for retries in case the errors are transient.
      if (elapsedPollingTime >= MAX_POLLING_DURATION_MS) {
        pollingTimedOutRef.current = true
        setActiveWorkflowLocationId(null)
        stopPolling()
      }
    } finally {
      isPollingInFlightRef.current = false
    }
  })

  const cancelPollingTimers = useCallback(() => {
    pollingStartedAtRef.current = null
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }
  }, [])

  const resetAtomForLocation = useCallback(() => {
    if (locationId) {
      setStartedByLocation(previousState => ({ ...previousState, [locationId]: false }))
    }
  }, [locationId, setStartedByLocation])

  const stopPolling = useCallback(() => {
    cancelPollingTimers()
    resetAtomForLocation()
  }, [cancelPollingTimers, resetAtomForLocation])

  const startPolling = useEffectEvent(() => {
    pollingStartedAtRef.current = Date.now()
    pollingIntervalRef.current = setInterval(poll, POLLING_INTERVAL_MS)
  })

  /**
   * Unconditionally resets pollingTimedOutRef whenever locationId changes.
   * This ensures that if the locationId changes, it always clears the
   * pollingTimedOutRef so it doesn't prevent polling for the new locationId.
   */
  useEffect(() => {
    pollingTimedOutRef.current = false
  }, [locationId])

  /**
   * Conditionally resets pollingTimedOutRef when startedByLocation[locationId] becomes true.
   * This handles the case where a user triggers a new provisioning workflow on the same location
   * after polling had previously timed out. Since locationId didn't change, the effect above
   * wouldn't fire — only this one catches it.
   */
  useEffect(() => {
    if (locationId && startedByLocation[locationId]) {
      pollingTimedOutRef.current = false
    }
  }, [locationId, startedByLocation])

  /**
   * This useEffect runs on mount, and when locationId changes, to check if there is an active provisioning workflow.
   * If there is, it starts polling if polling hasn't already been started.
   */
  useEffect(() => {
    if (locationId) {
      getWorkFlowsByStatus(locationId, WorkflowStatus.Running)
        .then(workflows => {
          if (workflows.length > 0 && !pollingStartedAtRef.current) {
            setActiveWorkflowLocationId(locationId)
            startPolling()
          }
        })
        .catch(error => {
          logger.error(`Error fetching workflows for locationId ${locationId} on mount`, error as Error)
        })
    }
  }, [getWorkFlowsByStatus, locationId])

  useEffect(() => {
    if (locationId) {
      /* 1. In this useEffect, we want to determine when to start polling for workflow status.
       * We start polling when any of the following are true:
       * - There are address discrepancies between the location and mspLocation.
       * - There are contact discrepancies between the location and mspLocation.
       * - the value of the `provisioningWorkflowStartedByLocationAtom` for this location is `true`
       */

      const workflowStartedByUser = Boolean(locationId && startedByLocation[locationId])

      // can only check discrepancies if we have both datasets
      const discrepancyExists =
        locationQuery.data && mspLocationQuery.data ? hasDiscrepancies(locationQuery.data, mspLocationQuery.data, true) : false

      const shouldStartPolling = !pollingTimedOutRef.current && (workflowStartedByUser || discrepancyExists || workflowIsActive)

      if (shouldStartPolling && !pollingStartedAtRef.current) {
        startPolling()
      }
    }
  }, [locationId, locationQuery.data, mspLocationQuery.data, startedByLocation, workflowIsActive])

  /**
   * The useEffect cleans up on unmount or when locationId changes.
   *
   * This runs on component unmount, and also before this effect re-runs if `cancelPollingTimers`
   * gets a new identity. In practice here that mainly means when `locationId` changes,
   * because `cancelPollingTimers` has no dependencies so it has a stable identity.
   */
  useEffect(() => {
    return () => {
      cancelPollingTimers()
    }
  }, [locationId, cancelPollingTimers])

  /* 5. return true if either of the following is true:
   * - `provisioningWorkflowStartedByLocationAtom` is `true`.
   * - There is an active provisioning workflow.
   */
  const workflowStartedByUser = Boolean(locationId && startedByLocation[locationId])
  return workflowStartedByUser || workflowIsActive
}
